import { useMemo, useState } from "react";
import GameShell from "../GameShell";

/**
 * Coral dos Animais — Phonological awareness.
 * 8 trials: show 4 animals, child picks the 2 whose names start with the same sound (first letter).
 */
export default function AnimalChoir() {
  return (
    <GameShell gameId="animal_choir" title="Coral dos Animais">
      {({ onFinish }) => <Game onFinish={onFinish} />}
    </GameShell>
  );
}

const ANIMALS = [
  { name: "Cachorro", emoji: "🐶" },
  { name: "Cavalo", emoji: "🐴" },
  { name: "Gato", emoji: "🐱" },
  { name: "Galinha", emoji: "🐔" },
  { name: "Macaco", emoji: "🐒" },
  { name: "Macaca", emoji: "🦧" },
  { name: "Pato", emoji: "🦆" },
  { name: "Porco", emoji: "🐷" },
  { name: "Tigre", emoji: "🐯" },
  { name: "Tartaruga", emoji: "🐢" },
  { name: "Rato", emoji: "🐭" },
  { name: "Raposa", emoji: "🦊" },
  { name: "Sapo", emoji: "🐸" },
  { name: "Serpente", emoji: "🐍" },
  { name: "Burro", emoji: "🐴" },
  { name: "Borboleta", emoji: "🦋" },
];

const TRIALS = 8;

function buildTrial() {
  // Pick a starting letter that has at least 2 animals
  const byLetter = {};
  for (const a of ANIMALS) {
    const L = a.name[0].toUpperCase();
    if (!byLetter[L]) byLetter[L] = [];
    byLetter[L].push(a);
  }
  const goodLetters = Object.keys(byLetter).filter((L) => byLetter[L].length >= 2);
  const letter = goodLetters[Math.floor(Math.random() * goodLetters.length)];
  const pair = [...byLetter[letter]].sort(() => Math.random() - 0.5).slice(0, 2);
  const distractorPool = ANIMALS.filter((a) => a.name[0].toUpperCase() !== letter);
  const distractors = distractorPool.sort(() => Math.random() - 0.5).slice(0, 2);
  const items = [...pair, ...distractors].sort(() => Math.random() - 0.5);
  return { letter, correct: pair.map((a) => a.name), items };
}

function Game({ onFinish }) {
  const [trialIdx, setTrialIdx] = useState(0);
  const [trial, setTrial] = useState(() => buildTrial());
  const [picked, setPicked] = useState([]);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [started] = useState(() => performance.now());
  const [tStart, setTStart] = useState(() => performance.now());
  const [reactionTimes, setReactionTimes] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const togglePick = (animal) => {
    if (feedback) return;
    if (picked.includes(animal.name)) {
      setPicked(picked.filter((n) => n !== animal.name));
      return;
    }
    const newPicked = [...picked, animal.name];
    setPicked(newPicked);
    if (newPicked.length === 2) {
      const rt = performance.now() - tStart;
      const correct = newPicked.every((n) => trial.correct.includes(n));
      setReactionTimes((r) => [...r, rt]);
      if (correct) setHits((h) => h + 1);
      else setMisses((m) => m + 1);
      setFeedback(correct ? "✅" : "❌");
      setTimeout(() => {
        const next = trialIdx + 1;
        if (next >= TRIALS) {
          const elapsed = performance.now() - started;
          const totalHits = hits + (correct ? 1 : 0);
          const totalMisses = misses + (correct ? 0 : 1);
          const avg = [...reactionTimes, rt].reduce((a, b) => a + b, 0) / (reactionTimes.length + 1);
          onFinish({
            score: Math.max(0, Math.round(totalHits * 12 - totalMisses * 4)),
            duration_ms: Math.round(elapsed),
            metrics: {
              hits: totalHits,
              misses: totalMisses,
              trials: TRIALS,
              avg_reaction_ms: Math.round(avg),
              accuracy: Number((totalHits / TRIALS).toFixed(3)),
            },
          });
        } else {
          setFeedback(null);
          setTrialIdx(next);
          setTrial(buildTrial());
          setPicked([]);
          setTStart(performance.now());
        }
      }, 600);
    }
  };

  return (
    <div className="mt-4 max-w-2xl mx-auto" data-testid="choir-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">Escolha 2 bichinhos cujos nomes começam com o mesmo som.</div>
        <div className="text-xs mt-1 opacity-80">Rodada {trialIdx + 1} / {TRIALS}</div>
      </div>
      <div className="kid-card pop-in" key={trialIdx}>
        <div className="grid grid-cols-2 gap-3">
          {trial.items.map((a, i) => {
            const sel = picked.includes(a.name);
            return (
              <button
                key={i}
                onClick={() => togglePick(a)}
                className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition active:scale-95 ${
                  sel ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-sky-50"
                }`}
                data-testid={`choir-opt-${i}`}
              >
                <div className="text-5xl">{a.emoji}</div>
                <div className="font-bold text-slate-800 text-lg">{a.name}</div>
              </button>
            );
          })}
        </div>
        {feedback && (
          <div className="text-center text-4xl mt-3 pop-in" data-testid="choir-feedback">
            {feedback}
          </div>
        )}
      </div>
    </div>
  );
}
